import { AbstractRepository } from "../repository";
import { Team } from "./teams-repository";

export class TeamSpace {
  constructor(
    public id: string,
    public ons_team_id: number,
    public name: string,
    public icon: string | null,
    public image: string | null,
    public createdAt: Date,
    public updatedAt: Date,
    public deletedAt: Date | null,
    public pages: TeamSpaceItem[],
    public mainPage: TeamSpaceItem|null,
    public permissions: TeamSpacePermisssion[],
  ) {}

  public static fromJson(json: any): TeamSpace {
    return new TeamSpace(
      json["id"],
      json["ons_team_id"],
      json["name"],
      json["icon"],
      json["image"],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
      json["deleted_at"] ? new Date(json[""]) : null,
      json["pages"] ? json["pages"].map((page: any) => TeamSpaceItem.fromJson(page)) : [],
      json["main_page"] ? TeamSpaceItem.fromJson(json['main_page']) : null,
      json["permissions"]
        ? json["permissions"].map((permission: any) => TeamSpacePermisssion.fromJson(permission))
        : [],
    );
  }
}

export class TeamSpaceItem {
  constructor(
    public id: string,
    public teamSpaceId: string,
    public parentItemId: string | null,
    public name: string,
    public type: string,
    public isDefault: boolean,
    public content: TeamSpacePageContent,
    public createdAt: Date,
    public updatedAt: Date,
    public versions: TeamSpacePageContent[],
  ) {}

  public static fromJson(json: any): TeamSpaceItem {
    return new TeamSpaceItem(
      json["id"],
      json["team_space_id"],
      json["parent_item_id"],
      json["name"],
      json['type'],
      json['is_default'],
      json["content"],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
      json["versions"] ? json["versions"].map((v: any) => TeamSpacePageContent.fromJson(v)) : [],
    );
  }
}

export class TeamSpaceTemplate {
  constructor(
    public id: string,
    public name: string,
    public content: string|null,
    public createdAt: Date,
    public updatedAt: Date,
  ) {
  }

  public static fromJson(json: any): TeamSpaceTemplate {
    return new TeamSpaceTemplate(
      json["id"],
      json["name"],
      json["content"],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}

export class TeamSpacePermisssion {
  constructor(
    public teamSpaceId: string,
    public onsTeamId: number | null,
    public type: "owner" | "editor" | "reader",
    public includeSubteams: boolean,
  ) {}

  public static fromJson(json: any): TeamSpacePermisssion {
    return new TeamSpacePermisssion(
      json["team_space_id"],
      json["ons_team_id"],
      json["type"],
      json['include_subteams']
    );
  }
}

export class TeamSpacePageContent {
  constructor(
    public id: string,
    public teamSpaceId: string,
    public teamSpacePageId: string,
    public content: string,
    public version: number,
    public isCurrentVersion: boolean,
    public editedBy: string,
    public createdAt: Date,
    public updatedAt: Date,
  ) {}

  public static fromJson(json: any): TeamSpacePageContent {
    return new TeamSpacePageContent(
      json["id"],
      json["team_space_id"],
      json["team_space_page_id"],
      json["content"],
      json["version"],
      json["is_current_version"],
      json["edited_by"],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}

export default class DocumentationLibraryRepository extends AbstractRepository {
  /**
   * Team Spaces
   */

  public async browseMyTeamSpaces(): Promise<Array<TeamSpace>> {
    let response = await this.request<{ data: { [id: number]: object } }>("GET", "documentation/spaces");
    return this.unpackDataCollectionResponse(response).map(ts => TeamSpace.fromJson(ts));
  }

  public async browseMyTeamSpacesWithMainPage(): Promise<Array<TeamSpace>> {
    let response = await this.request<{ data: { [id: number]: object } }>("GET", "documentation/spaces?withMain=true");
    return this.unpackDataCollectionResponse(response).map(ts => TeamSpace.fromJson(ts));
  }

  public async readTeamSpace(teamSpaceId: string): Promise<TeamSpace> {
    let response = await this.request<{ data: object }>("GET", `documentation/spaces/${teamSpaceId}`);
    return TeamSpace.fromJson(response.data);
  }

  public async addTeamSpace(onsTeamId: number, name: string, image: File | null): Promise<TeamSpace> {
    let response = await this.request<{ data: object }>("POST", `documentation/spaces`, {
      ons_team_id: onsTeamId,
      name: name,
      ...(image != null ? { image: image } : {}),
    });
    return TeamSpace.fromJson(response.data);
  }

  public async editTeamSpace(
    teamSpaceId: string,
    onsTeamId: number,
    name: string,
    icon?: string,
    image?: string,
    permissions?: TeamSpacePermisssion[],
  ): Promise<TeamSpace> {
    let response = await this.request<{ data: object }>("PUT", `documentation/spaces/${teamSpaceId}`, {
      ons_team_id: onsTeamId,
      name: name,
      ...(icon != undefined ? { icon: icon } : {}),
      ...(image != undefined ? { image: image } : {}),
      ...(permissions != undefined
        ? { permissions: permissions.map((x) => ({ ons_team_id: x.onsTeamId, type: x.type, include_subteams: x.includeSubteams })) }
        : {}),
    });
    return TeamSpace.fromJson(response.data);
  }

  public async editTeamSpaceImage(teamSpaceId: string, image: File): Promise<TeamSpace> {
    let response = await this.request<{ data: object }>("PUT", `documentation/spaces/${teamSpaceId}`, image);
    return TeamSpace.fromJson(response.data);
  }

  public async deleteTeamSpace(teamSpaceId: string): Promise<void> {
    await this.request("DELETE", `documentation/spaces/${teamSpaceId}`);
  }

  /**
   * Team Space Items
   */

  public async addPage(teamSpaceId: string, name: string, type: string, parentItemId: string | null, content: string | null): Promise<TeamSpaceItem> {
    let response = await this.request<{ data: object }>("POST", `documentation/spaces/${teamSpaceId}/items`, {
      team_space_id: teamSpaceId,
      name: name,
      type: type,
      ...(parentItemId != null ? { parent_item_id: parentItemId } : {}),
      ...(content != null ? { content: content } : {}),
    });
    return TeamSpaceItem.fromJson(response.data);
  }

  public async updatePage(teamSpaceId: string, teamSpaceItemId: string, name: string): Promise<TeamSpaceItem> {
    const response = await this.request<{data: object}>('PUT', `documentation/spaces/${teamSpaceId}/items/${teamSpaceItemId}`, {
      name: name
    });
    return TeamSpaceItem.fromJson(response.data)
  }

  public async updateFolder(teamSpaceId: string, teamSpaceItemId: string, name: string): Promise<TeamSpaceItem> {
    const response = await this.request<{data: object}>('PUT', `documentation/spaces/${teamSpaceId}/items/${teamSpaceItemId}`, {
      name: name
    });
    return TeamSpaceItem.fromJson(response.data)
  }

  public async addFolder(teamSpaceId: string, name: string, type: string, parentItemId?: string): Promise<TeamSpaceItem> {
    const response = await this.request<{data: object}>('POST', `documentation/spaces/${teamSpaceId}/items`, {
      team_space_id: teamSpaceId,
      name: name,
      type: type,
      ...(parentItemId != undefined ? {parent_item_id: parentItemId} : {})
    });
    return TeamSpaceItem.fromJson(response.data)
  }

  public async deletePage(pageId: string): Promise<void> {
    await this.request("DELETE", `documentation/pages/${pageId}`);
  }

  public async deleteFolder(teamSpaceId: string, itemId: string): Promise<void> {
    await this.request('DELETE', `documentation/spaces/${teamSpaceId}/items/${itemId}`)
  }

  public async browsePagesForTeamSpace(teamSpaceId: string): Promise<TeamSpaceItem[]> {
    let response = await this.request<{ data: object[] }>("GET", `documentation/spaces/${teamSpaceId}/pages`);
    return response.data.map((page) => TeamSpaceItem.fromJson(page));
  }

  public async readPage(pageId: string): Promise<TeamSpaceItem> {
    let response = await this.request<{ data: object }>("GET", `documentation/pages/${pageId}`);
    return TeamSpaceItem.fromJson(response.data);
  }

  public async updatePageContent(pageId: string, content: string, shouldNotify?: boolean): Promise<TeamSpaceItem> {
    let response = await this.request<{ data: object }>("PUT", `documentation/pages/${pageId}`, {
      content: content,
      ...(shouldNotify ? {should_notify: shouldNotify} : {}),
    });
    return TeamSpaceItem.fromJson(response.data);
  }
  public async setDefaultPage(pageId: string): Promise<void> {
    await this.request<{ data: object }>("PUT", `documentation/pages/${pageId}/set-default`, {
    });
  }

  public async revertPageVersion(pageId: string, versionId: string): Promise<TeamSpacePageContent> {
    let response = await this.request<{ data: object }>("PUT", `documentation/pages/${pageId}/version/${versionId}`);
    return TeamSpacePageContent.fromJson(response.data);
  }

  public async deletePageVersion(pageId: string, versionId: string): Promise<void> {
    await this.request("DELETE", `documentation/pages/${pageId}/version/${versionId}`);
  }

  public async browseTemplates(): Promise<TeamSpaceTemplate[]> {
    const response = await this.request<{data: object[]}>('GET', 'documentation/templates');
    return response.data.map((template) => TeamSpaceTemplate.fromJson(template))
  }

  public async addTemplate(name: string): Promise<TeamSpaceTemplate> {
    const response = await this.request<{data: object}>('POST', 'documentation/templates', {
      name: name,
    })
    return TeamSpaceTemplate.fromJson(response.data)
  }

  public async updateTemplate(templateId: string, name: string, content: string|null): Promise<TeamSpaceTemplate> {
    const response = await this.request<{data: object}>('PUT', `documentation/templates/${templateId}`, {
      name: name,
      content: content,
    })
    return TeamSpaceTemplate.fromJson(response.data)
  }

  public async deleteTemplate(templateId: string): Promise<void> {
    await this.request<{data: object[]}>('GET', `documentation/templates/${templateId}`);
  }
}
